import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { blogPosts, getPostBySlug } from '../data/blogPosts';

/* ══ BLOG DETAIL PAGE ══ */
const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = getPostBySlug(slug ?? '');

  useEffect(() => {
    if (post) document.title = `${post.title} | Azura Mobility`;
  }, [post]);

  if (!post) return <Navigate to="/news" replace />;

  const related = blogPosts.filter(p => p.slug !== post.slug && p.category === post.category).slice(0, 3);
  const fallbackRelated = related.length > 0 ? related : blogPosts.filter(p => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-bg-cream text-bg-dark antialiased">

      {/* ══ HERO ══ */}
      <section className="relative bg-bg-dark overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-accent-amber/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(255,184,0,0.07) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)' }} />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 py-24">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 px-4 py-2 mb-6">
            <Link to="/" className="text-white/60 hover:text-accent-amber transition text-xs font-semibold">Home</Link>
            <span className="text-white/70">/</span>
            <Link to="/news" className="text-white/60 hover:text-accent-amber transition text-xs font-semibold">News</Link>
            <span className="text-white/70">/</span>
            <span className="text-accent-amber text-xs font-semibold">{post.category}</span>
          </div>

          <span className="inline-block bg-accent-amber text-bg-dark text-xs font-figtree font-black uppercase tracking-widest px-3 py-1.5 rounded-full mb-5">
            {post.category}
          </span>

          <h1 className="text-3xl md:text-5xl font-figtree font-black text-white leading-tight mb-6">
            {post.title}
          </h1>
        </div>
      </section>

      {/* ══ FEATURE IMAGE ══ */}
      <div className="max-w-5xl mx-auto px-6  relative z-10">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-[1.75rem] overflow-hidden shadow-[0_32px_80px_rgba(30,58,47,0.2)] border border-white/10 aspect-[16/8]">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        </motion.div>
      </div>

      {/* ══ CONTENT ══ */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto grid grid-cols-1 gap-12">
          <div>
            <p className="font-exo text-lg text-neutral-700 leading-relaxed mb-8 pb-8 border-b border-border italic">
              {post.excerpt}
            </p>

            <div className="space-y-6">
              {post.content.map((para, i) => (
                <motion.p key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.5, delay: Math.min(i * 0.04, 0.2) }}
                  className="font-exo text-base text-neutral-600 leading-8">
                  {para}
                </motion.p>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-border flex flex-wrap items-center justify-between gap-4">
              <Link to="/news" className="inline-flex items-center gap-2 text-bg-dark font-figtree font-bold text-xs uppercase tracking-wider hover:text-accent-amber transition-colors">
                <ArrowLeft size={13} /> All Articles
              </Link>
              <Link to="/invest" className="inline-flex items-center gap-2 bg-bg-dark text-white px-6 py-3 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-accent-amber hover:text-bg-dark transition-all shadow-capsule">
                Explore AZT <ArrowRight size={13} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══ RELATED ══ */}
      <section className="py-20 px-6 bg-white border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-10">
            <div className="flex items-center -space-x-1">
              <div className="w-3 h-3 rounded-full bg-accent-amber" />
              <div className="w-3 h-3 rounded-full bg-bg-dark border-l border-bg-cream" />
            </div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/50">Keep Reading</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {fallbackRelated.map(p => (
              <Link key={p.slug} to={`/news/${p.slug}`} className="group flex flex-col bg-neutral-50 border border-border rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover hover:-translate-y-1.5 transition-all duration-300">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/40 to-transparent" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <span className="text-xs font-figtree font-black uppercase tracking-widest text-accent-amber mb-2">{p.category}</span>
                  <h3 className="font-figtree font-black text-base text-bg-dark tracking-tight leading-snug mb-3 group-hover:text-accent-amber transition-colors flex-1">
                    {p.title}
                  </h3>
                  <span className="inline-flex items-center gap-1.5 text-xs font-figtree font-bold text-bg-dark group-hover:text-accent-amber transition-colors">
                    Read Article <ArrowRight size={11} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section className="py-20 px-6 bg-bg-dark border-t-4 border-accent-amber text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-figtree font-black text-3xl md:text-4xl text-white tracking-tight mb-4">
            Like what you're reading? <span className="text-accent-amber italic">Join the vision.</span>
          </h2>
          <p className="font-exo text-white/70 text-sm leading-relaxed mb-8 max-w-xl mx-auto">
            The AZT Strategic Round opens October 15 — get in at the founding price.
          </p>
          <Link to="/invest"
            className="inline-flex items-center gap-2 bg-accent-amber text-bg-dark px-7 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all shadow-amber-sm">
            Join the Vision <ArrowRight size={13} />
          </Link>
        </div>
      </section>

    </div>
  );
};

export default BlogDetail;
